/**
 * AiExtractBox — Pannello riutilizzabile per estrazione AI da un allegato.
 *
 * Props:
 *   tipo          - "fattura" | "contratto_subappalto" | "generico" | "bolla"
 *                   ("bolla" instrada al vecchio endpoint /bolle/estrai-dati)
 *   allegato      - data URL base64 del file (foto o PDF); null/undefined disabilita
 *   onExtracted   - (dati) => void — callback quando i campi sono pronti
 *   auto          - bool (default true) — estrae automaticamente al cambio di allegato
 *   compact       - bool — layout ridotto (una riga)
 *   label         - stringa custom (default dipende dal tipo)
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import api from "@/api";

const LABELS = {
  fattura: "Compila i dati fattura dall'allegato con AI",
  contratto_subappalto: "Compila anagrafica subappalto dall'allegato con AI",
  bolla: "Compila i dati bolla dall'allegato con AI",
  generico: "Estrai dati dal documento con AI",
};

export default function AiExtractBox({
  tipo = "generico",
  allegato,
  onExtracted,
  auto = true,
  compact = false,
  label,
}) {
  const [loading, setLoading] = useState(false);
  const doneRef = useRef(new Set());
  const aiToastRef = useRef(null);

  const runExtract = async (dataUrl, isAuto = false) => {
    if (!dataUrl) return;
    setLoading(true);
    const t = toast.loading(isAuto ? "AI: leggo l'allegato…" : "Analisi AI…");
    aiToastRef.current = t;
    try {
      const url = tipo === "bolla" ? "/bolle/estrai-dati" : `/ai/estrai?tipo=${tipo}`;
      const { data } = await api.post(
        url,
        { allegato: dataUrl },
        { timeout: 45000 }
      );
      const dati = data?.dati || {};
      const nonEmpty = Object.values(dati).filter((v) => v !== "" && v !== 0 && v != null).length;
      toast.dismiss(t);
      if (nonEmpty === 0) {
        toast.warning("L'AI non è riuscita a leggere l'allegato. Riprova con una foto più nitida o compila manualmente.");
      } else {
        toast.success(`✓ ${nonEmpty} campi compilati dall'AI — verifica e completa`);
      }
      onExtracted?.(dati, data?.fornitore_match || null);
    } catch (e) {
      toast.dismiss(t);
      toast.error(e.response?.data?.detail || "Estrazione AI non riuscita");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (aiToastRef.current !== null) toast.dismiss(aiToastRef.current);
    };
  }, []);

  useEffect(() => {
    if (!auto || !allegato || doneRef.current.has(allegato) || loading) return;
    doneRef.current.add(allegato);
    runExtract(allegato, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allegato]);

  if (!allegato) return null;

  return (
    <div
      className={`flex items-center justify-between gap-2 ${compact ? "p-2" : "p-3"} bg-violet-50 border-2 border-violet-300 rounded-sm mt-2`}
      data-testid={`ai-extract-box-${tipo}`}
    >
      <div className="text-xs text-violet-900 flex-1">
        {loading ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <b>AI in corso…</b> sto leggendo il documento
          </span>
        ) : (
          <>
            <b>{label || LABELS[tipo]}</b>
            <span className="hidden sm:inline"> — puoi comunque modificare i campi manualmente</span>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          doneRef.current.delete(allegato);
          runExtract(allegato, false);
        }}
        disabled={loading}
        data-testid={`btn-ai-extract-${tipo}`}
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-xs font-semibold rounded-sm whitespace-nowrap"
      >
        {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analizzo…</> : <><Sparkles className="w-3.5 h-3.5" /> Ri-estrai</>}
      </button>
    </div>
  );
}
